import type { Ring, Vec2 } from '../../../geo/types';
import type { ReservedFootprint } from '../types';
import committed from '../../../data/lastageYard.json';

/*
 * The goods Anthonisz's 1544 woodcut draws in the Lastage's timber yard, along the Geldersekade
 * spit, read on the scan and put in layout metres by tools/lastage (docs/plans/lastage.md).
 * Sizes are on the one picture scale the woodcut's goods share (tools/quayGoods/sources/picture_scale.json).
 */
export const YARD_KINDS = ['boxStack', 'plankPile', 'logStack', 'beamHeap', 'heap', 'kettle'] as const;
export type YardKind = (typeof YARD_KINDS)[number];

interface YardBase {
  readonly id: string;
  /** The layout point under the middle of the item's foot. */
  readonly at: Vec2;
  /** Where the georeference put it, when that was not free Lastage ground. */
  readonly shift?: { readonly from: Vec2; readonly metres: number };
  readonly sourceRefs: readonly string[];
}

export interface YardBlock extends YardBase {
  readonly kind: 'boxStack' | 'plankPile';
  /** The long axis, radians from east, counter-clockwise. */
  readonly yaw: number;
  readonly length: number;
  readonly width: number;
  readonly height: number;
}

export interface LogTier {
  readonly count: number;
  readonly length: number;
}

export interface YardLogs extends YardBase {
  readonly kind: 'logStack';
  readonly yaw: number;
  readonly diameter: number;
  /** From the bottom up; every tier's logs end flush at the stack's +u end, as the woodcut draws their ends. */
  readonly tiers: readonly LogTier[];
}

export interface YardBeam {
  /** The beam's middle from the heap's `at`, in layout metres. */
  readonly offset: Vec2;
  readonly yaw: number;
  readonly length: number;
  /** The height of its underside: the top of the beam it lies across, or the ground. */
  readonly base: number;
}

export interface YardBeams extends YardBase {
  readonly kind: 'beamHeap';
  readonly section: number;
  readonly beams: readonly YardBeam[];
}

export interface YardHeap extends YardBase {
  readonly kind: 'heap';
  readonly diameter: number;
  readonly height: number;
}

/** A round kettle on three legs; the woodcut draws a fire and smoke under and over it. */
export interface YardKettle extends YardBase {
  readonly kind: 'kettle';
  readonly diameter: number;
  readonly potHeight: number;
  readonly legHeight: number;
}

export type YardItem = YardBlock | YardLogs | YardBeams | YardHeap | YardKettle;

function fail(message: string): never {
  throw new Error(`lastageYard.json: ${message}`);
}

function positive(v: unknown, what: string): number {
  if (typeof v !== 'number' || !Number.isFinite(v) || v <= 0) fail(`${what} must be a positive number`);
  return v;
}

function finite(v: unknown, what: string): number {
  if (typeof v !== 'number' || !Number.isFinite(v)) fail(`${what} must be a number`);
  return v;
}

function point(v: unknown, what: string): Vec2 {
  if (!Array.isArray(v) || v.length !== 2) fail(`${what} must be a point`);
  return [finite(v[0], what), finite(v[1], what)];
}

type Raw = Record<string, unknown>;

function parseKind(raw: Raw, base: YardBase, id: string): YardItem {
  switch (raw.kind) {
    case 'boxStack':
    case 'plankPile':
      return {
        ...base,
        kind: raw.kind,
        yaw: finite(raw.yaw, `${id}.yaw`),
        length: positive(raw.length, `${id}.length`),
        width: positive(raw.width, `${id}.width`),
        height: positive(raw.height, `${id}.height`),
      };
    case 'logStack':
      return { ...base, kind: 'logStack', yaw: finite(raw.yaw, `${id}.yaw`), diameter: positive(raw.diameter, `${id}.diameter`), tiers: parseTiers(raw.tiers, id) };
    case 'beamHeap':
      return { ...base, kind: 'beamHeap', section: positive(raw.section, `${id}.section`), beams: parseBeams(raw.beams, id) };
    case 'heap':
      return { ...base, kind: 'heap', diameter: positive(raw.diameter, `${id}.diameter`), height: positive(raw.height, `${id}.height`) };
    case 'kettle':
      return {
        ...base,
        kind: 'kettle',
        diameter: positive(raw.diameter, `${id}.diameter`),
        potHeight: positive(raw.potHeight, `${id}.potHeight`),
        legHeight: positive(raw.legHeight, `${id}.legHeight`),
      };
    default:
      return fail(`${id}: unknown kind ${String(raw.kind)}`);
  }
}

function parseTiers(v: unknown, id: string): LogTier[] {
  if (!Array.isArray(v) || v.length === 0) fail(`${id}.tiers must be a non-empty list`);
  return v.map((t: Raw, i) => ({
    count: Number.isInteger(t.count) && (t.count as number) > 0 ? (t.count as number) : fail(`${id}.tiers[${i}].count`),
    length: positive(t.length, `${id}.tiers[${i}].length`),
  }));
}

function parseBeams(v: unknown, id: string): YardBeam[] {
  if (!Array.isArray(v) || v.length === 0) fail(`${id}.beams must be a non-empty list`);
  return v.map((b: Raw, i) => {
    const base = finite(b.base, `${id}.beams[${i}].base`);
    if (base < 0) fail(`${id}.beams[${i}].base must not be negative`);
    return {
      offset: point(b.offset, `${id}.beams[${i}].offset`),
      yaw: finite(b.yaw, `${id}.beams[${i}].yaw`),
      length: positive(b.length, `${id}.beams[${i}].length`),
      base,
    };
  });
}

function parseItem(raw: Raw): YardItem {
  const id = typeof raw.id === 'string' ? raw.id : fail('an item has no id');
  const shift = raw.shift as Raw | undefined;
  const base: YardBase = {
    id,
    at: point(raw.at, `${id}.at`),
    ...(shift ? { shift: { from: point(shift.from, `${id}.shift.from`), metres: positive(shift.metres, `${id}.shift.metres`) } } : {}),
    sourceRefs: Array.isArray(raw.sourceRefs) ? (raw.sourceRefs as string[]) : fail(`${id}.sourceRefs`),
  };
  return parseKind(raw, base, id);
}

export function parseLastageYard(file: unknown): readonly YardItem[] {
  const items = (file as { items?: unknown }).items;
  if (!Array.isArray(items)) fail('items must be a list');
  const parsed = items.map((raw) => parseItem(raw as Raw));
  const ids = new Set(parsed.map((i) => i.id));
  if (ids.size !== parsed.length) fail('item ids must be unique');
  return parsed;
}

export const LASTAGE_YARD: readonly YardItem[] = parseLastageYard(committed);

function rect(centre: Vec2, yaw: number, length: number, width: number): Ring {
  const [c, s] = [Math.cos(yaw), Math.sin(yaw)];
  const [l, w] = [length / 2, width / 2];
  return ([[-l, -w], [l, -w], [l, w], [-l, w]] as const).map(
    ([u, v]): Vec2 => [centre[0] + u * c - v * s, centre[1] + u * s + v * c]
  );
}

function circle(centre: Vec2, radius: number, segments: number): Ring {
  return Array.from({ length: segments }, (_, i): Vec2 => {
    const a = (2 * Math.PI * i) / segments;
    return [centre[0] + radius * Math.cos(a), centre[1] + radius * Math.sin(a)];
  });
}

/** Segments of a heap's cone, and of its footprint. */
export const HEAP_SEGMENTS = 12;
/** Segments of a kettle's pot, and of its footprint. */
export const KETTLE_SEGMENTS = 8;

/** Free ground kept round every item, as the generator keeps it from walks, streets and each other. */
export const YARD_CLEARANCE = 0.5;

/** The item's ground outline, grown by `margin`: one ring, or one per beam of a heap of beams. */
export function yardFootprints(item: YardItem, margin = 0): readonly Ring[] {
  const m = 2 * margin;
  switch (item.kind) {
    case 'heap':
      return [circle(item.at, item.diameter / 2 + margin, HEAP_SEGMENTS)];
    case 'kettle':
      return [circle(item.at, item.diameter / 2 + margin, KETTLE_SEGMENTS)];
    case 'beamHeap':
      return item.beams.map((b) =>
        rect([item.at[0] + b.offset[0], item.at[1] + b.offset[1]], b.yaw, b.length + m, item.section + m)
      );
    case 'logStack': {
      const bottom = item.tiers[0]!;
      return [rect(item.at, item.yaw, bottom.length + m, bottom.count * item.diameter + m)];
    }
    default:
      return [rect(item.at, item.yaw, item.length + m, item.width + m)];
  }
}

/* A heap's or kettle's ring is inscribed in its circle, so its reservation takes the circumscribed one. */
function reservationRings(item: YardItem): readonly Ring[] {
  if (item.kind !== 'heap' && item.kind !== 'kettle') return yardFootprints(item, YARD_CLEARANCE);
  const segments = item.kind === 'heap' ? HEAP_SEGMENTS : KETTLE_SEGMENTS;
  const r = (item.diameter / 2 + YARD_CLEARANCE) / Math.cos(Math.PI / segments);
  return [circle(item.at, r, segments)];
}

/** The goods' ground with its clearance, for the lot planner to leave free. */
export function yardReservations(items: readonly YardItem[]): readonly ReservedFootprint[] {
  return items.flatMap((item) =>
    reservationRings(item).map((outer, i) => ({ id: `lastageYard:${item.id}:${i}`, polygon: { outer, holes: [] } }))
  );
}
